/**
 * Premium first-class marketing section components — used both by the
 * original hand-built HomePage AND registered as builder blocks so admins
 * can edit copy/query/order/visibility without decomposing them into
 * generic primitives.
 *
 * Each component accepts a `props` bag of editable fields. Defaults match
 * the pre-Phase-8 homepage exactly.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, ShieldCheck, Sparkles, Zap, MessageSquare, Wallet, Users, Star } from "lucide-react";
import HeroSearchBar from "@/components/marketplace/HeroSearchBar";
import CategoryCard from "@/components/marketplace/CategoryCard";
import ProviderCard from "@/components/marketplace/ProviderCard";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const DEFAULT_HERO_IMG = "https://images.unsplash.com/photo-1600210492493-0946911123ea?auto=format&fit=crop&w=2000&q=80";
const cx = (...c) => c.filter(Boolean).join(" ");

// ---------- HERO WITH SEARCH -----------------------------------------
export function GcwHeroSection({ props = {}, mode = "view" }) {
  const { user } = useAuth();
  const {
    eyebrow = "Serving the Gulf Coast — FL · AL · MS · LA · TX",
    headline_prefix = "Get Any Kind of",
    headline_accent = "Work Done.",
    subheadline = "Book local professionals, hire digital experts, or post a custom project — from one trusted marketplace built for the Gulf Coast.",
    background_image = DEFAULT_HERO_IMG,
    ctas = [
      { key: "local", icon: "map-pin", title: "Book Local", subtitle: "Cleaners, handymen, movers, lawn care — same day.", href: "/book-local", accent: "from-[#165a71] to-[#0f3d4d]" },
      { key: "digital", icon: "sparkles", title: "Hire Digital", subtitle: "Designers, developers, marketers, editors.", href: "/hire-digital", accent: "from-[#252A34] to-[#071b22]" },
      { key: "request", icon: "message-square", title: "Request a Service", subtitle: "Describe the job — pros come to you with quotes.", href: "/request-a-service", accent: "from-[#37768e] to-[#165a71]", requires_auth: true },
    ],
  } = props;

  const iconFor = (name) => ({ "map-pin": MapPin, "sparkles": Sparkles, "message-square": MessageSquare }[name] || MapPin);

  return (
    <section className="relative -mt-24 min-h-[86vh] flex items-center justify-center overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${background_image})` }} aria-hidden />
      <div className="absolute inset-0 bg-gradient-to-b from-[#071b22]/85 via-[#071b22]/70 to-[#071b22]/85" aria-hidden />
      <div className="absolute inset-0 gcw-noise opacity-40" aria-hidden />
      <div className="relative mx-auto max-w-6xl px-6 lg:px-10 pt-40 pb-24 w-full">
        <div className="max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur-md px-3.5 py-1.5 text-xs font-medium text-white/85">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            {eyebrow}
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-semibold text-white tracking-tight leading-[1.05]" data-testid="hero-headline">
            {headline_prefix} <br className="hidden md:block" />
            <span className="text-[#87bccc]">{headline_accent}</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="mt-5 text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed">
            {subheadline}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="mt-10">
            {mode === "edit" ? (
              <div className="rounded-2xl bg-white/95 backdrop-blur-md p-2 shadow-2xl">
                <div className="rounded-xl bg-white px-4 py-3 text-sm text-slate-500">🔎  Search bar (live in public render)</div>
              </div>
            ) : (
              <HeroSearchBar />
            )}
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }} className="mt-10 grid gap-4 sm:grid-cols-3 items-stretch">
            {ctas.map((m) => {
              const Icon = iconFor(m.icon);
              const href = m.requires_auth && !user ? `/login?next=${encodeURIComponent(m.href)}` : m.href;
              const inner = (
                <div className={cx("group relative h-full flex flex-col overflow-hidden rounded-2xl p-5 text-white border border-white/10 hover:-translate-y-0.5 transition-transform duration-300 bg-gradient-to-br", m.accent || "from-[#165a71] to-[#0f3d4d]")}>
                  <div className="absolute inset-0 gcw-noise opacity-20" aria-hidden />
                  <div className="relative flex items-center justify-between">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 backdrop-blur-md ring-1 ring-white/20 group-hover:bg-white/25 transition-colors">
                      <Icon className="h-5 w-5" />
                    </span>
                    <ArrowRight className="h-4 w-4 text-white/70 group-hover:translate-x-1 transition-transform" />
                  </div>
                  <div className="relative mt-4">
                    <div className="font-display font-semibold text-lg leading-snug">{m.title}</div>
                    <div className="text-sm text-white/80 mt-1 leading-relaxed">{m.subtitle}</div>
                  </div>
                </div>
              );
              return mode === "edit"
                ? <div key={m.key} data-testid={`cta-${m.key}`} className="h-full">{inner}</div>
                : <Link key={m.key} to={href} data-testid={`cta-${m.key}`} className="block h-full">{inner}</Link>;
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ---------- STATS STRIP ----------------------------------------------
export function GcwStatsStrip({ props = {} }) {
  const {
    stat_endpoint = "/system/stats",
    labels = { providers: "Verified pros", categories: "Service categories", locations: "Gulf Coast locations", users: "Community members" },
  } = props;
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get(stat_endpoint).then(r => setStats(r.data)).catch(() => {});
  }, [stat_endpoint]);
  if (!stats) return null;
  return (
    <section className="border-y border-slate-200 bg-white" data-testid="stats-strip">
      <div className="mx-auto max-w-6xl px-6 lg:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
        {Object.entries(labels).map(([key, label]) => (
          <div key={key}>
            <div className="font-display text-3xl font-semibold text-[#071b22]" data-testid={`stat-${key}`}>{stats[key] ?? "—"}</div>
            <div className="mt-1 text-xs text-[#252A34]/60 uppercase tracking-wider">{label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------- CATEGORIES SECTION ---------------------------------------
export function GcwCategoriesSection({ props = {}, mode = "view" }) {
  const {
    eyebrow = "Explore",
    headline = "Popular categories on the coast",
    cta_label = "All categories",
    cta_href = "/categories",
    // Query mixes local + digital featured roots.
    limit = 12,
    show_local = true,
    show_digital = true,
  } = props;
  const [items, setItems] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const requests = [];
        if (show_local) requests.push(api.get("/categories", { params: { kind: "local", featured_only: true, parent_id: "root" } }));
        if (show_digital) requests.push(api.get("/categories", { params: { kind: "digital", featured_only: true, parent_id: "root" } }));
        const results = await Promise.all(requests);
        const merged = results.flatMap(r => r.data).slice(0, limit);
        setItems(merged);
      } catch (e) {}
    })();
  }, [limit, show_local, show_digital]);
  return (
    <section className="py-24" data-testid="categories-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">{eyebrow}</div>
            <h2 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-[#071b22]">{headline}</h2>
          </div>
          {mode === "edit" ? (
            <span className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-[#165a71]">{cta_label} <ArrowRight className="h-4 w-4" /></span>
          ) : (
            <Link to={cta_href} className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-[#165a71] hover:text-[#11485a]" data-testid="all-categories-link">
              {cta_label} <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {items.map((c, i) => (
            <CategoryCard key={c.id} category={c} index={i} disableLink={mode === "edit"} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- FEATURED PROVIDERS SECTION -------------------------------
export function GcwFeaturedProviders({ props = {}, mode = "view" }) {
  const {
    eyebrow = "Featured pros",
    headline = "Meet Gulf Coast standouts",
    cta_label = "Browse all pros",
    cta_href = "/providers",
    limit = 8,
    sort = "featured",
  } = props;
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/providers", { params: { limit, sort } }).then(r => setItems(r.data.items || [])).catch(() => {});
  }, [limit, sort]);
  return (
    <section className="py-24 bg-white border-y border-slate-200" data-testid="featured-providers-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">{eyebrow}</div>
            <h2 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-[#071b22]">{headline}</h2>
          </div>
          {mode === "edit" ? (
            <span className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-[#165a71]">{cta_label} <ArrowRight className="h-4 w-4" /></span>
          ) : (
            <Link to={cta_href} className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-[#165a71] hover:text-[#11485a]" data-testid="all-providers-link">
              {cta_label} <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {items.map((p, i) => (
            <ProviderCard key={p.id} provider={p} index={i} disableLink={mode === "edit"} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- HOW IT WORKS SECTION -------------------------------------
export function GcwHowItWorks({ props = {}, mode = "view" }) {
  const {
    eyebrow = "How it works",
    headline = "One marketplace. Three ways to get things done.",
    lede = "Whether it's a leaking faucet in Pensacola or a rebrand for your Baton Rouge coffee shop — Gulf Coast Work keeps the whole workflow (search, hire, chat, pay, review) in one place.",
    cta_label = "Create your free account",
    cta_href = "/register",
    steps = [
      { icon: "users", title: "Tell us what you need", copy: "Search a category, describe the job, or start with a template. Real, verified pros on both sides — no lead-broker games." },
      { icon: "wallet", title: "Book, request quotes, or hire", copy: "Instant booking for local pros, custom quotes for projects, or long-form gigs like Fiverr — all under one roof." },
      { icon: "shield-check", title: "Get it done — with peace of mind", copy: "Escrow-protected payments, ID-verified pros, ratings you can trust, and dispute support if anything goes sideways." },
    ],
  } = props;
  const iconFor = (name) => ({ "users": Users, "wallet": Wallet, "shield-check": ShieldCheck, "sparkles": Sparkles, "zap": Zap }[name] || Users);
  return (
    <section className="py-24" data-testid="how-it-works-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-12 lg:grid-cols-12 items-start">
        <div className="lg:col-span-4">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">{eyebrow}</div>
          <h2 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-[#071b22]">{headline}</h2>
          <p className="mt-5 text-[#252A34]/75 leading-relaxed">{lede}</p>
          {mode === "edit" ? (
            <span className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#071b22] text-white px-6 py-3 font-medium">{cta_label} <ArrowRight className="h-4 w-4" /></span>
          ) : (
            <Link to={cta_href} data-testid="how-cta-signup" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#071b22] text-white px-6 py-3 font-medium hover:bg-black transition-colors">
              {cta_label} <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>
        <div className="lg:col-span-8 grid gap-6 sm:grid-cols-1">
          {steps.map((s, i) => {
            const Icon = iconFor(s.icon);
            return (
              <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.5, delay: i * 0.1 }} className="flex gap-5 rounded-2xl bg-white border border-slate-200 p-6 hover:border-[#165a71]/30 transition-colors">
                <div className="h-11 w-11 shrink-0 rounded-xl bg-[#165a71]/10 text-[#165a71] flex items-center justify-center">
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-display text-lg font-semibold text-[#071b22]">
                    {i + 1}. {s.title}
                  </div>
                  <p className="mt-1.5 text-[#252A34]/75 leading-relaxed">{s.copy}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---------- BECOME A PROVIDER CTA -----------------------------------
export function GcwBecomeProviderCta({ props = {}, mode = "view" }) {
  const {
    eyebrow = "For pros",
    headline = "Get discovered by real customers on the coast.",
    body = "Whether you clean pool decks in Destin or design brands from Baton Rouge — build a profile once, and get booked from every angle.",
    primary_cta = { label: "Become a provider", href: "/register?intended_role=provider" },
    secondary_cta = { label: "How it works", href: "/become-a-provider" },
    callouts = [
      { icon: "zap", title: "Zero listing fees", copy: "Only pay when you actually get hired." },
      { icon: "shield-check", title: "Buyer-protected", copy: "Escrow, insurance & dispute support." },
      { icon: "users", title: "One profile", copy: "Local, digital or agency — unified." },
      { icon: "sparkles", title: "Free growth tools", copy: "Promotions, referrals & featured slots." },
    ],
  } = props;
  const iconFor = (name) => ({ "zap": Zap, "shield-check": ShieldCheck, "users": Users, "sparkles": Sparkles, "star": Star }[name] || Zap);
  return (
    <section className="py-24" data-testid="become-provider-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="relative overflow-hidden rounded-3xl bg-[#071b22] text-white p-10 md:p-16">
          <div className="absolute inset-0 gcw-noise opacity-30" aria-hidden />
          <div className="absolute -right-10 -top-10 h-64 w-64 rounded-full bg-[#165a71]/40 blur-3xl" aria-hidden />
          <div className="relative grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#87bccc]">{eyebrow}</div>
              <h2 className="mt-2 font-display text-3xl md:text-4xl font-semibold">{headline}</h2>
              <p className="mt-5 text-white/75 max-w-lg leading-relaxed">{body}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                {mode === "edit" ? (
                  <>
                    <span className="inline-flex items-center gap-2 rounded-full bg-white text-[#071b22] px-6 py-3 font-medium">{primary_cta.label} <ArrowRight className="h-4 w-4" /></span>
                    <span className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 font-medium">{secondary_cta.label}</span>
                  </>
                ) : (
                  <>
                    <Link to={primary_cta.href} className="inline-flex items-center gap-2 rounded-full bg-white text-[#071b22] px-6 py-3 font-medium hover:bg-slate-100 transition-colors" data-testid="become-pro-cta">
                      {primary_cta.label} <ArrowRight className="h-4 w-4" />
                    </Link>
                    <Link to={secondary_cta.href} className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 font-medium hover:bg-white/10 transition-colors" data-testid="become-pro-learn-more">
                      {secondary_cta.label}
                    </Link>
                  </>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {callouts.map((c, i) => {
                const Icon = iconFor(c.icon);
                return (
                  <div key={i} className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md p-4">
                    <Icon className="h-5 w-5 text-[#87bccc]" />
                    <div className="mt-3 font-display font-medium">{c.title}</div>
                    <div className="text-xs text-white/70 mt-1 leading-relaxed">{c.copy}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Trust & verification (new premium section) --------------
export function GcwTrustSection({ props = {}, mode = "view" }) {
  const {
    eyebrow = "Trust built in",
    headline = "Every pro. Every job. Every dollar. Protected.",
    lede = "Verification badges you can see, escrow payments you can trust, and a real trust-and-safety team when things get complicated.",
    items = [
      { icon: "shield-check", title: "Identity + background verified", copy: "Every pro submits ID and, in high-trust categories, a criminal background check." },
      { icon: "wallet", title: "Escrow-protected payments", copy: "Your card is authorized at booking; funds only release when the work is done and approved." },
      { icon: "users", title: "24/7 trust & safety", copy: "Real humans review reports, mediate disputes, and pause suspicious accounts fast." },
      { icon: "star", title: "Reviews you can trust", copy: "Only completed, paid bookings can leave reviews. No drive-by trolling." },
    ],
  } = props;
  const iconFor = (name) => ({ "shield-check": ShieldCheck, "wallet": Wallet, "users": Users, "star": Star, "sparkles": Sparkles, "zap": Zap }[name] || ShieldCheck);
  return (
    <section className="py-24 bg-white border-y border-slate-200" data-testid="trust-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="max-w-2xl">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">{eyebrow}</div>
          <h2 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-[#071b22]">{headline}</h2>
          <p className="mt-4 text-[#252A34]/75 leading-relaxed">{lede}</p>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {items.map((it, i) => {
            const Icon = iconFor(it.icon);
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.4, delay: i * 0.08 }} className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="h-11 w-11 rounded-xl bg-[#165a71]/10 text-[#165a71] flex items-center justify-center">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="mt-4 font-display text-lg font-semibold text-[#071b22]">{it.title}</div>
                <p className="mt-1.5 text-[#252A34]/75 text-sm leading-relaxed">{it.copy}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
